
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star, Zap, Crown } from "lucide-react";
import { Link } from "react-router-dom";

const pricingTiers = [
  {
    id: "basic",
    name: "Basic",
    price: "$9.99",
    period: "month",
    description: "Perfect for getting started",
    features: [
      "Up to 100 memories per month",
      "Basic voice assistant",
      "Standard integrations",
      "Email support",
    ],
    icon: Star,
    color: "from-blue-500 to-cyan-500",
    popular: false,
  },
  {
    id: "premium",
    name: "Premium",
    price: "$19.99",
    period: "month",
    description: "Most popular choice for power users",
    features: [
      "Unlimited memories",
      "Advanced AI voice assistant",
      "Premium integrations",
      "Priority support",
      "Advanced analytics",
      "Custom categories",
    ],
    icon: Zap,
    color: "from-purple-500 to-violet-500",
    popular: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$39.99",
    period: "month",
    description: "For teams and advanced users",
    features: [
      "Everything in Premium",
      "Team collaboration",
      "API access",
      "White-label options",
      "Dedicated support",
      "Advanced security",
      "Custom integrations",
    ],
    icon: Crown,
    color: "from-amber-500 to-orange-500",
    popular: false,
  },
];

const Upgrade = () => {
  const { currentUser } = useAuth();
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async (tierId: string) => {
    if (!currentUser) {
      // Redirect to login if not authenticated
      return;
    }

    setIsLoading(true);
    setSelectedTier(tierId);

    try {
      // TODO: Implement Stripe checkout integration
      console.log(`Upgrading to ${tierId} tier`);
      
      // Placeholder for Stripe checkout
      // const { data } = await supabase.functions.invoke('create-checkout', {
      //   body: { priceId: tier.priceId }
      // });
      // window.open(data.url, '_blank');
      
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setIsLoading(false);
      setSelectedTier(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <Link to="/" className="inline-block mb-8">
            <Button variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              ← Back to Home
            </Button>
          </Link>
          
          <h1 className="text-5xl font-bold text-white mb-4">
            Upgrade Your Experience
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Choose the perfect plan to unlock the full potential of Shared Matter
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricingTiers.map((tier) => {
            const IconComponent = tier.icon;
            return (
              <Card 
                key={tier.id}
                className={`relative bg-gray-800/50 border-gray-700 text-white transition-all duration-300 hover:scale-105 ${
                  tier.popular ? 'ring-2 ring-purple-500 shadow-2xl shadow-purple-500/20' : ''
                }`}
              >
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-violet-500 text-white">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${tier.color} flex items-center justify-center`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>
                  <CardDescription className="text-gray-400">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{tier.price}</span>
                    <span className="text-gray-400">/{tier.period}</span>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-3">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  <Button
                    onClick={() => handleUpgrade(tier.id)}
                    disabled={isLoading && selectedTier === tier.id}
                    className={`w-full bg-gradient-to-r ${tier.color} hover:opacity-90 text-white font-semibold py-3 transition-all duration-200`}
                  >
                    {isLoading && selectedTier === tier.id ? (
                      "Processing..."
                    ) : (
                      `Upgrade to ${tier.name}`
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">
                Can I change plans anytime?
              </h3>
              <p className="text-gray-300">
                Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
              </p>
            </div>
            
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">
                Is there a free trial?
              </h3>
              <p className="text-gray-300">
                We offer a 14-day free trial for all paid plans. No credit card required to start.
              </p>
            </div>
            
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">
                What happens to my data if I downgrade?
              </h3>
              <p className="text-gray-300">
                Your data is always safe. If you exceed limits on a lower plan, you'll still have access but won't be able to add more until you upgrade.
              </p>
            </div>
            
            <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-3">
                Do you offer team discounts?
              </h3>
              <p className="text-gray-300">
                Yes! Contact our sales team for custom pricing on team and enterprise plans with 10+ users.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upgrade;
